import { useMutation } from '@apollo/client/react'
import { Button, notification, Upload } from 'antd'
import { parse } from 'papaparse'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { SetCustomerPaywallDocument } from '~app/graph/generated/graphql'

/*
 * CSV Format
 */

interface CSVColumn {
  customerId: string
  isPaywalled: boolean
}

interface CSVRow {
  customerID: string
  isPaywalled: string
}

const schema = z.object({
  customerId: z.string().min(1),
  isPaywalled: z.boolean(),
})

/*
 * Component
 */

const UploadCSVButton = () => {
  const { t } = useTranslation()

  const [setCustomerPaywall] = useMutation(SetCustomerPaywallDocument)

  /**
   * triggers mutations based on CSV data
   */
  const setPaywallStatus = (columns: CSVColumn[]) => {
    return Promise.all(
      columns.map(async ({ customerId, isPaywalled }) => {
        try {
          const result = await setCustomerPaywall({
            variables: { id: customerId, isPaywalled },
          })

          // missing return data: means FE customer fragment will become out of date
          // should not happen according to schema
          if (!result || !result.data) {
            console.warn(result)
            throw new Error('missing result data')
          }

          // operation error at level of GQL operation, update failed
          if (result.error) {
            throw new Error(result.error.message)
          }

          // update success
          return {
            data: {
              customerId,
              paywalledAt: result.data.setCustomerPaywall.paywalledAt,
            },
          }
        } catch (error) {
          console.error(error)
          return { error: { customerId } }
        }
      })
    )
  }

  return (
    <Upload
      showUploadList={false}
      beforeUpload={
        /**
         * Parse CSV data, then trigger paywall mutations
         */
        fileContent => {
          parse<CSVRow>(fileContent, {
            header: true,
            skipEmptyLines: true,
            delimiter: ',',
            complete: async parseResults => {
              const rows = parseResults.data
              const columns = rows.reduce<{
                parsed: CSVColumn[]
                invalid: CSVRow[]
              }>(
                (result, row) => {
                  const parsedData = {
                    customerId: row.customerID,
                    isPaywalled:
                      // convert true/false from string to boolean
                      row.isPaywalled === 'true'
                        ? true
                        : row.isPaywalled === 'false'
                          ? false
                          : // falback for other values
                            row.isPaywalled,
                  }
                  const validation = schema.safeParse(parsedData)
                  return validation.success
                    ? // When valid, we store the parsed data into `parsed`
                      {
                        parsed: [...result.parsed, validation.data],
                        invalid: result.invalid,
                      }
                    : // When invalid, we store the raw row data into `invalid`
                      {
                        parsed: result.parsed,
                        invalid: [...result.invalid, row],
                      }
                },
                {
                  parsed: [],
                  invalid: [],
                }
              )

              // if there are invalid rows in the uploaded CSV, the process stops
              // and we provide a file containing the rows that were not accepted
              if (columns.invalid.length > 0) {
                alert(t('paywallCustomer.uploadCSV.error.fileFormat'))
                // Log to console for easier debugging
                console.error(
                  `Format error, ensure CSV format is
  
                    customerID,isPaywalled
                    1234,true
                    5678,false
                    `
                )
                console.error('---- invalid columns:', columns.invalid)
              }

              // When there are no invalid rows in the uploaded CSV, we trigger mutations
              // Then, we check for failures
              // If there are failure cases, display a notification
              // showing which IDs failed to update
              else {
                const mutationResults = await setPaywallStatus(columns.parsed)

                // extract errors if any and display notification
                const errors: string[] = mutationResults.reduce(
                  (failedCustomerIds, result) =>
                    result.error?.customerId
                      ? [...failedCustomerIds, result.error?.customerId]
                      : failedCustomerIds,
                  [] as string[]
                )

                if (errors.length) {
                  notification.error({
                    duration: 0, // do not close auto
                    message: t('paywallCustomer.uploadCSV.error.update'),
                    description: (
                      <div>
                        {errors.map(customerId => (
                          <div key={customerId}>{customerId}</div>
                        ))}
                      </div>
                    ),
                  })
                } else {
                  notification.success({
                    message: t('paywallCustomer.uploadCSV.success'),
                  })
                }
              }
            },
          })
        }
      }
      accept="text/csv"
    >
      <Button type="primary">{t('paywallCustomer.uploadCSV.button')}</Button>
    </Upload>
  )
}

export default UploadCSVButton
